/**
 * 场景二：购物车页面 - 合并查询 API
 *
 * 将原本 7 个串行请求合并为 3 步串行：
 * - Step 1: shoppingCartSettlement (Mutation，保持不变，使用原有 fetchSettlementShoppingCart)
 * - Step 2: fetchCartInit (2 合 1：getShoppingCart + activityCountdownByCart)
 * - Step 3: fetchCartExtras (6 合 1：orderPromotionResult + checkProductCanCheckout + freeGiftByOrder + markUpByOrder + getActiveCoupons + getOrderIsIncludeMemberPrice)
 */
import { fetcher } from '@/services/graphql/fetcher';
import {
  CART_INIT_WITH_PICKUP_QUERY,
  CART_INIT_WITHOUT_PICKUP_QUERY,
  CART_EXTRAS_QUERY,
} from '@/services/graphql/docs';
import { useAppStore } from '@/store';

/**
 * CartInit 返回数据类型
 */
export interface CartInitData {
  getShoppingCart: {
    shoppingTrolley: AnyObject | null;
    outrightPurchase: AnyObject | null;
  };
  activityCountdownByCart: {
    activityCountdown: AnyObject | null;
    activityTag: string | null;
  } | null;
}

/**
 * CartExtras 返回数据类型（6 合 1）
 */
export interface CartExtrasData {
  orderPromotionResult: AnyObject[] | null;
  checkProductCanCheckout: {
    canCheckout: boolean;
    reason: string | null;
    errorDetail: AnyObject[];
  } | null;
  freeGiftByOrder: AnyObject[] | null;
  markUpByOrder: AnyObject[] | null;
  // 6 合 1 新增字段
  getActiveCoupons: {
    availableCoupons: AnyObject[];
    couponsAreNotAvailable: AnyObject[];
    lackOfExchangeGoodsCoupons: AnyObject[];
  } | null;
  getOrderIsIncludeMemberPrice: boolean | null;
}

/**
 * Step 2: 获取购物车核心数据（2 合 1）
 *
 * 合并的接口：
 * - getShoppingCart (购物车和预下单信息)
 * - activityCountdownByCart (购物车活动倒计时)
 *
 * @param isRemoveMarkUp 是否移除加价购
 * @param isUseMember 是否使用会员价
 * @param isUseShoppingCredit 是否使用购物金
 * @param isRemoveGuessLike 是否移除猜你喜欢
 */
export const fetchCartInit = async (
  isRemoveMarkUp: boolean = false,
  isUseMember: boolean = true,
  isUseShoppingCredit: boolean = true,
  isRemoveGuessLike: boolean = true
): Promise<CartInitData | null> => {
  const appStore = useAppStore();
  const enablePickup = appStore.channelConfig?.enablePickup ?? false;
  const document = enablePickup ? CART_INIT_WITH_PICKUP_QUERY : CART_INIT_WITHOUT_PICKUP_QUERY;

  const res = await fetcher.query(document, {
    variables: {
      isRemoveMarkUp,
      isUseMember,
      isUseShoppingCredit,
      isRemoveGuessLike,
    },
  });

  const data = res.data.value;
  if (data) {
    return {
      getShoppingCart: data.getShoppingCart ?? { shoppingTrolley: null, outrightPurchase: null },
      activityCountdownByCart: data.activityCountdownByCart ?? null,
    };
  }
  return null;
};

/**
 * Step 3: 获取购物车附加数据（4 合 1）
 *
 * 合并的接口：
 * - orderPromotionResult (订单促销优惠结果)
 * - checkProductCanCheckout (商品限购校验)
 * - freeGiftByOrder (赠品信息)
 * - markUpByOrder (加价购信息)
 *
 * 需要从 Step 2 的 getShoppingCart 返回中提取参数
 *
 * @param orderIds 订单 ID 数组（购物车 + 预下单）
 * @param orderId 主订单 ID（用于查询赠品和加价购）
 * @param skuIds SKU ID 数组
 * @param skuQuantities SKU 数量数组
 */
export const fetchCartExtras = async (
  orderIds: string[],
  orderId: string,
  skuIds: string[],
  skuQuantities: { skuId: string; quantity: number }[]
): Promise<CartExtrasData | null> => {
  const res = await fetcher.query(CART_EXTRAS_QUERY, {
    variables: {
      orderIds,
      orderId,
      skuIds,
      skuQuantities,
    },
  });

  const data = res.data.value;
  if (data) {
    return {
      orderPromotionResult: data.orderPromotionResult ?? null,
      checkProductCanCheckout: data.checkProductCanCheckout ?? null,
      freeGiftByOrder: data.freeGiftByOrder ?? null,
      markUpByOrder: data.markUpByOrder ?? null,
      // 6 合 1 新增字段
      getActiveCoupons: data.getActiveCoupons ?? null,
      getOrderIsIncludeMemberPrice: data.getOrderIsIncludeMemberPrice ?? null,
    };
  }
  return null;
};

/**
 * 从 getShoppingCart 结果中提取 CartExtras 所需的参数
 */
export const extractCartExtrasParams = (
  shoppingCart: CartInitData['getShoppingCart']
): {
  orderIds: string[];
  orderId: string;
  skuIds: string[];
  skuQuantities: { skuId: string; quantity: number }[];
} | null => {
  const shoppingTrolley = shoppingCart?.shoppingTrolley;
  const outrightPurchase = shoppingCart?.outrightPurchase;

  if (!shoppingTrolley?.id) {
    return null;
  }

  const orderIds: string[] = [];
  if (shoppingTrolley?.id) orderIds.push(shoppingTrolley.id);
  if (outrightPurchase?.id) orderIds.push(outrightPurchase.id);

  const orderId = shoppingTrolley.id;

  const lines = shoppingTrolley.lines ?? [];
  const skuIds = lines.map((line: AnyObject) => line.productVariant?.id).filter(Boolean);
  const skuQuantities = lines
    .filter((line: AnyObject) => line.productVariant?.id)
    .map((line: AnyObject) => ({
      skuId: line.productVariant.id,
      quantity: line.quantity,
    }));

  return {
    orderIds,
    orderId,
    skuIds,
    skuQuantities,
  };
};

